import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { useLocation, useNavigate } from "react-router-dom";
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import activitiesData from '../assets/data/activities.json';
import '../assets/css/activities.css';

const Activities = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const pathname = location.pathname;
    const hash = location.hash;
    const key = location.key;

    useEffect(() => {
        if (hash === '') {
            document.getElementById("body")?.scrollTo(0, 0);
        } else {
            setTimeout(() => {
                const id = hash.replace('#', '');
                const element = document.getElementById(id);
                if (element) {
                    element.scrollIntoView();
                }
            }, 0);
        }
    }, [pathname, hash, key]);

    const [selectedCategory, setSelectedCategory] = useState('all');
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Process JSON data into unified activity format
    const processActivities = () => {
        const allActivities = [];
        let idCounter = 1;

        const categoryDisplayNames = {
            'self_reliance_activities': 'Self Reliance',
            'health_activities': 'Health',
            'education_activities': 'Education',
            'donation': 'Donation',
            'social_activity': 'Social Activity'
        };

        Object.keys(activitiesData).forEach(categoryKey => {
            const categoryDisplayName = categoryDisplayNames[categoryKey] || categoryKey;

            activitiesData[categoryKey].forEach(activity => {
                allActivities.push({
                    id: idCounter++,
                    title: activity.title,
                    description: activity.short_desc || activity.full_desc?.substring(0, 150) + '...',
                    fullDescription: activity.full_desc,
                    category: activity.category.toLowerCase().replace(/ /g, '-'),
                    date: activity.date || "2025",
                    location: activity.location || "India",
                    originalCategory: activity.category,
                    readTime: "5 min read",
                    images: activity.images || [] // Images array from JSON
                });
            });
        });

        return allActivities;
    };

    const activities = processActivities();

    const getCategories = () => {
        const categorySet = new Set();
        activities.forEach(activity => {
            categorySet.add(activity.originalCategory);
        });

        return [
            { id: 'all', name: 'All' },
            ...Array.from(categorySet).sort().map(cat => ({
                id: cat.toLowerCase().replace(/ /g, '-'),
                name: cat
            }))
        ];
    };

    const categories = getCategories();

    // Handle URL query parameters for category filtering
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const categoryParam = params.get('category');

        if (categoryParam) {
            // Check if the category exists in our categories list
            const categoryExists = categories.some(cat => cat.id === categoryParam);
            if (categoryExists) {
                setSelectedCategory(categoryParam);
            }
        }
    }, [location.search]);

    const filteredActivities = selectedCategory === 'all'
        ? activities
        : activities.filter(activity => activity.category === selectedCategory);

    const handleCategoryChange = (categoryId) => {
        setSelectedCategory(categoryId);

        // Update URL with category parameter
        if (categoryId === 'all') {
            navigate('/activities', { replace: true });
        } else {
            navigate(`/activities?category=${categoryId}`, { replace: true });
        }
    };

    const handleReadMore = (activity) => {
        setSelectedActivity(activity);
        setCurrentImageIndex(0);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedActivity(null);
        setCurrentImageIndex(0);
    };

    const handleNextImage = () => {
        if (selectedActivity && selectedActivity.images.length > 0) {
            setCurrentImageIndex((prev) =>
                (prev + 1) % selectedActivity.images.length
            );
        }
    };

    const handlePrevImage = () => {
        if (selectedActivity && selectedActivity.images.length > 0) {
            setCurrentImageIndex((prev) =>
                prev === 0 ? selectedActivity.images.length - 1 : prev - 1
            );
        }
    };

    const getCategoryColor = (category) => {
        const colors = {
            'education': '#3b82f6',
            'health': '#ef4444',
            'healthcare': '#ef4444',
            'self-reliance': '#10b981',
            'social-activity': '#f59e0b',
            'community-events': '#f59e0b',
            'donation': '#8b5cf6',
            'community-support': '#8b5cf6',
            'relief-work': '#ec4899',
            'food-support': '#06b6d4',
            'cultural-activity': '#f97316',
            'women-empowerment': '#d946ef',
            'empowerment': '#d946ef',
            'environment': '#22c55e',
            'social-welfare': '#6366f1',
            'patriotic-activity': '#f43f5e',
            'religious-activity': '#a855f7'
        };
        return colors[category] || '#6b7280';
    };

    useEffect(() => {
        if (!modalOpen || !selectedActivity) return;

        const images =
            selectedActivity.images && selectedActivity.images.length > 0
                ? selectedActivity.images
                : getPlaceholderImages(selectedActivity.category);

        if (images.length <= 1) return;

        const interval = setInterval(() => {
            setFade(false); // start fade-out

            setTimeout(() => {
                setCurrentImageIndex((prev) => (prev + 1) % images.length);
                setFade(true); // fade-in
            }, 300);
        }, 3000); // ⏱️ auto-scroll speed

        return () => clearInterval(interval);
    }, [modalOpen, selectedActivity]);

    // Placeholder images for carousel when no images are available
    const getPlaceholderImages = (category) => {
        return [
            `https://via.placeholder.com/800x500/3b82f6/ffffff?text=${encodeURIComponent('Activity Image 1')}`,
            `https://via.placeholder.com/800x500/10b981/ffffff?text=${encodeURIComponent('Activity Image 2')}`,
            `https://via.placeholder.com/800x500/f59e0b/ffffff?text=${encodeURIComponent('Activity Image 3')}`
        ];
    };
    const [fade, setFade] = useState(true);


    return (
        <div className='bg-accent'>
            <section id='activities-breadcrumb'>
                <Container>
                    <div className='activities-breadcrumb'>
                        <nav aria-label="breadcrumb">
                            <h3 className='breadcrumb-heading'>Our Activities</h3>
                        </nav>
                    </div>
                </Container>
            </section>

            <section id='activities-hero'>
                <div className='activities-hero-container'>
                    <h2 className='text-center'>Transforming Lives Through Action</h2>
                    <p className='activities-hero-subtitle text-center px-3 mx-auto'>
                        Discover the transformative work we do in communities across India.
                        From education to healthcare, each activity reflects our commitment to creating positive change.
                    </p>

                    <Card>
                        <CardContent>
                            <div className='filter-chips'>
                                {categories.map((category) => (
                                    <Chip
                                        key={category.id}
                                        label={category.name}
                                        onClick={() => handleCategoryChange(category.id)}
                                        color={selectedCategory === category.id ? "primary" : "default"}
                                        variant={selectedCategory === category.id ? "filled" : "outlined"}
                                        className='filter-chip'
                                    />
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {filteredActivities.length > 0 ? (
                        <Grid container spacing={3}>
                            {filteredActivities.map((activity) => (
                                <Grid item xs={12} sm={6} md={4} key={activity.id}>
                                    <Card className='activity-card'>
                                        <div
                                            className="activity-image-container"
                                            style={{
                                                height: '200px',
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            <img
                                                src={
                                                    activity.images && activity.images.length > 0
                                                        ? activity.images[0]
                                                        : 'https://via.placeholder.com/400x200?text=No+Image'
                                                }
                                                alt={activity.title}
                                                loading="lazy"
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                            />

                                            {/* Category Badge */}
                                            <div className="activity-category-badge">
                                                {activity.originalCategory}
                                            </div>
                                        </div>

                                        <CardContent>
                                            <div className='activity-meta'>
                                                <span className='activity-meta-item'>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                                        <line x1="16" y1="2" x2="16" y2="6"></line>
                                                        <line x1="8" y1="2" x2="8" y2="6"></line>
                                                        <line x1="3" y1="10" x2="21" y2="10"></line>
                                                    </svg>
                                                    {activity.date}
                                                </span>
                                            </div>
                                            <h4 className='activity-title'>{activity.title}</h4>
                                            <p className='activity-description'>{activity.description}</p>
                                            <div className='activity-footer'>
                                                <div className='activity-location'>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                                        <circle cx="12" cy="10" r="3"></circle>
                                                    </svg>
                                                    {activity.location}
                                                </div>
                                                <Button
                                                    variant="text"
                                                    color="primary"
                                                    className='read-more-btn'
                                                    size="small"
                                                    onClick={() => handleReadMore(activity)}
                                                >
                                                    Read More →
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <div className='empty-state'>
                            <h3>No Activities Found</h3>
                            <p>Try selecting a different category to see more activities.</p>
                        </div>
                    )}
                </div>
            </section>

            {/* Modal for Activity Details */}
            <Dialog
                open={modalOpen}
                onClose={handleCloseModal}
                maxWidth="md"
                fullWidth
            >
                {selectedActivity && (
                    <>
                        <DialogTitle>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                    <h3 style={{ margin: 0, marginBottom: '8px' }}>{selectedActivity.title}</h3>
                                    <Chip
                                        label={selectedActivity.originalCategory}
                                        size="small"
                                        style={{
                                            backgroundColor: getCategoryColor(selectedActivity.category),
                                            color: 'white'
                                        }}
                                    />
                                </div>
                                <IconButton
                                    onClick={handleCloseModal}
                                    style={{ marginTop: '-8px', marginRight: '-8px' }}
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </IconButton>
                            </div>
                        </DialogTitle>
                        <DialogContent dividers>
                            {/* Image Carousel */}
                            <div style={{ position: 'relative', marginBottom: '24px' }}>
                                <div style={{
                                    width: '100%',
                                    height: '400px',
                                    backgroundColor: '#f3f4f6',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    position: 'relative'
                                }}>
                                    <img
                                        src={
                                            selectedActivity.images && selectedActivity.images.length > 0
                                                ? selectedActivity.images[currentImageIndex]
                                                : getPlaceholderImages(selectedActivity.category)[currentImageIndex]
                                        }
                                        alt={`${selectedActivity.title} - Image ${currentImageIndex + 1}`}
                                        className={`activity-carousel-img ${fade ? 'fade-in' : 'fade-out'}`}
                                    />

                                    {/* Carousel Controls */}
                                    {((selectedActivity.images && selectedActivity.images.length > 1) ||
                                        (!selectedActivity.images || selectedActivity.images.length === 0)) && (
                                            <>
                                                <IconButton
                                                    onClick={handlePrevImage}
                                                    style={{
                                                        position: 'absolute',
                                                        left: '16px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                                    }}
                                                >
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="15 18 9 12 15 6"></polyline>
                                                    </svg>
                                                </IconButton>
                                                <IconButton
                                                    onClick={handleNextImage}
                                                    style={{
                                                        position: 'absolute',
                                                        right: '16px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                                    }}
                                                >
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="9 18 15 12 9 6"></polyline>
                                                    </svg>
                                                </IconButton>

                                                {/* Image Indicators */}
                                                <div style={{
                                                    position: 'absolute',
                                                    bottom: '16px',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    display: 'flex',
                                                    gap: '8px'
                                                }}>
                                                    {(selectedActivity.images && selectedActivity.images.length > 0
                                                        ? selectedActivity.images
                                                        : getPlaceholderImages(selectedActivity.category)
                                                    ).map((_, index) => (
                                                        <div
                                                            key={index}
                                                            style={{
                                                                width: '8px',
                                                                height: '8px',
                                                                borderRadius: '50%',
                                                                backgroundColor: index === currentImageIndex
                                                                    ? 'white'
                                                                    : 'rgba(255, 255, 255, 0.5)',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.3s'
                                                            }}
                                                            onClick={() => setCurrentImageIndex(index)}
                                                        />
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                </div>
                            </div>

                            {/* Activity Details */}
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{
                                    display: 'flex',
                                    gap: '24px',
                                    marginBottom: '16px',
                                    flexWrap: 'wrap'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                            <line x1="16" y1="2" x2="16" y2="6"></line>
                                            <line x1="8" y1="2" x2="8" y2="6"></line>
                                            <line x1="3" y1="10" x2="21" y2="10"></line>
                                        </svg>
                                        <span>{selectedActivity.date}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                            <circle cx="12" cy="10" r="3"></circle>
                                        </svg>
                                        <span>{selectedActivity.location}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Full Description */}
                            <div style={{ lineHeight: '1.8', color: '#374151' }}>
                                <p>{selectedActivity.fullDescription}</p>
                            </div>
                            <div className='text-center'>
                                <Button onClick={handleCloseModal} color="primary" variant="contained">
                                    Close
                                </Button>
                            </div>
                        </DialogContent>

                    </>
                )}
            </Dialog>
        </div>
    );
};

export default Activities;